import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';

interface CommitNumberFieldProps {
  value: number | undefined;
  onCommit: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}

const CommitNumberField: React.FC<CommitNumberFieldProps> = ({
  value,
  onCommit,
  step = 1,
  min,
  max,
  placeholder,
}) => {
  const initial =
    value === undefined || Number.isNaN(value) ? '' : String(Number(value.toFixed(2)));
  const [draft, setDraft] = useState<string>(initial);
  const draftRef = useRef<string>(draft);
  const cancelledRef = useRef<boolean>(false);
  const commitRef = useRef<(next: string) => void>(() => {});
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  commitRef.current = (next: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (next === '') return;
    const v = parseFloat(next);
    if (Number.isNaN(v)) return;
    if (v === value) return;
    onCommit(v);
  };

  useEffect(
    () => () => {
      commitRef.current(draftRef.current);
    },
    [],
  );

  const commit = () => commitRef.current(draftRef.current);

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(initial);
    inputRef.current?.blur();
  };

  return (
    <Input
      ref={inputRef}
      type="number"
      value={draft}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
};

export default CommitNumberField;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 4px 6px;
  font-size: 13px;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #9ca3af;
  }
`;
